import {
  TopbarNavigationMFEProps,
  TopbarNavigationSkeleton,
} from '@cw-elements/host-kit';
import { loadRemoteModule } from '@cw-elements/mfe-setup/load';
import { PLATFORM_DATA_CONFIG, ROUTES } from 'common/constants';
import { AVAILABLE_LANGUAGES } from 'common/constants/user-options.constant';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { CropwiseAiButton, CropwiseAiContainer } from 'src/pages/cropwise-ai';
import {
  NotificationButton,
  NotificationsContainer,
} from 'src/pages/notifications';
import { useNotificationUnreadCount } from 'src/pages/notifications/notifications.hook';

import { useTopbarNavigation } from './topbar-navigation.hook';
import { TopbarNavigationWrapper } from './topbar-navigation.style';

const TopBarNavigationMFE = lazy(() =>
  loadRemoteModule<TopbarNavigationMFEProps>('topbar-navigation/index'),
);

const TopbarNavigation = () => {
  const { cropwiseAiHeaderButton, headerNotification } = useFlags();
  const notificationUnreadCount = useNotificationUnreadCount();
  const {
    userLanguage,
    handleOnChangeLanguage,
    onClickGoToMyCropwise,
    onEntitiesSelect,
    handleLogout,
    currentOrganizationId,
    topbarOrganizationId,
    topbarWorkspaceId,
    currentFarmExternalId,
    featureConfig,
    entitiesSelectorAdditionalData,
    handleOpenCropwiseAi,
    setShowType,
    showType,
    isNotificationsOpen,
    handleToggleNotifications,
    handleCloseNotifications,
  } = useTopbarNavigation();

  const { t } = useTranslation();

  return (
    <TopbarNavigationWrapper>
      <Suspense
        fallback={<TopbarNavigationSkeleton featureConfig={featureConfig} />}
      >
        <TopBarNavigationMFE
          featureConfig={featureConfig}
          defaultItems={{
            selectedFarm: {
              value: currentFarmExternalId || '',
            },
            selectedOrganization: {
              value: topbarOrganizationId || '',
            },
            selectedWorkspace: {
              value: topbarWorkspaceId || '',
            },
          }}
          productImage={{
            redirectUrl: ROUTES.MY_CROPWISE,
            imageSrc: '/assets/cropwise-balance-logo.svg',
          }}
          appData={{
            id: PLATFORM_DATA_CONFIG.APP_ID,
            name: 'balance',
          }}
          userLanguage={userLanguage}
          onLogout={handleLogout}
          profileDropdownConfig={() => ({
            defaultLanguage: 'pt-BR',
            availableLanguages: AVAILABLE_LANGUAGES,
            onLanguageChange: handleOnChangeLanguage,
            availableUnitSystems: [],
          })}
          settingsMenuConfig={() => ({
            workspaceId: topbarWorkspaceId ?? undefined,
            orgId: topbarOrganizationId ?? undefined,
            propertyId: currentFarmExternalId ?? undefined,
          })}
          organizationDropdown={() => ({
            goToEntity: onClickGoToMyCropwise,
          })}
          farmDropdown={() => ({
            goToEntity: onClickGoToMyCropwise,
          })}
          workspaceDropdown={() => ({
            goToEntity: onClickGoToMyCropwise,
          })}
          entitiesSelectorOnNavigate={onEntitiesSelect}
          entitiesSelectorAdditionalData={entitiesSelectorAdditionalData}
          additionalItems={
            currentOrganizationId
              ? () => [
                  ...(cropwiseAiHeaderButton
                    ? [
                        {
                          tooltipText: t('cropwise_ai.button.tooltip'),
                          onClick: handleOpenCropwiseAi,
                          icon: (
                            <CropwiseAiButton
                              text={t('cropwise_ai.button.open')}
                            />
                          ),
                        },
                      ]
                    : []),
                  ...(headerNotification
                    ? [
                        {
                          tooltipText: t('notification_web.title'),
                          onClick: handleToggleNotifications,
                          icon: (
                            <NotificationButton
                              hasUnread={notificationUnreadCount > 0}
                            />
                          ),
                        },
                      ]
                    : []),
                ]
              : undefined
          }
        />
      </Suspense>
      <CropwiseAiContainer setShowType={setShowType} showType={showType} />
      <NotificationsContainer
        isOpen={isNotificationsOpen}
        onClose={handleCloseNotifications}
      />
    </TopbarNavigationWrapper>
  );
};

export { TopbarNavigation };
